import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  items: [],
  totalAmount: 0,
};

const MAX_QUANTITY = 20;

function selectedAttributesKey(selectedAttributes) {
  if (!selectedAttributes || typeof selectedAttributes !== 'object') return '';
  try {
    const keys = Object.keys(selectedAttributes).sort();
    return keys.map((k) => `${k}:${String(selectedAttributes[k] ?? '')}`).join('|');
  } catch (_) {
    return '';
  }
}

function cartItemKey(item) {
  return [
    item.productId ?? item.productID,
    item.rent_days ?? 1,
    item.event_date ?? '',
    selectedAttributesKey(item.selectedAttributes),
  ].join('::');
}

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addToCart(state, action) {
      const product = action.payload;
      const productId = product.productId ?? product.productID;
      const rent_days = product.rent_days ?? 1;
      const event_date = product.event_date ?? "";
      const selectedAttributes = product.selectedAttributes ?? null;
      const payload = {
        ...product,
        productId,
        productID: productId,
        rent_days,
        event_date,
        size: product.size ?? null,
        selectedAttributes: selectedAttributes && typeof selectedAttributes === 'object' ? selectedAttributes : null,
      };
      const attrsKey = selectedAttributesKey(selectedAttributes);
      const existingItem = state.items.find(
        (item) =>
          (item.productId ?? item.productID) === productId &&
          (item.rent_days ?? 1) === rent_days &&
          (item.event_date ?? "") === event_date &&
          selectedAttributesKey(item.selectedAttributes) === attrsKey
      );
      if (existingItem) {
        if (existingItem.quantity < MAX_QUANTITY) {
          existingItem.quantity += 1;
          state.totalAmount += payload.productPrice ?? 0;
        }
      } else {
        state.items.push({ ...payload, quantity: 1 });
        state.totalAmount += payload.productPrice ?? 0;
      }
    },
    updateQuantity(state, action) {
      const { productID, productId, rent_days, event_date, quantity, selectedAttributes } = action.payload;
      const id = productId ?? productID;
      const attrsKey = selectedAttributesKey(selectedAttributes);
      const itemToUpdate = state.items.find(
        (item) =>
          (item.productId ?? item.productID) === id &&
          (item.rent_days ?? 1) === (rent_days ?? 1) &&
          (item.event_date ?? "") === (event_date ?? "") &&
          selectedAttributesKey(item.selectedAttributes) === attrsKey
      );
      if (itemToUpdate) {
        const difference = quantity - itemToUpdate.quantity;
        if (quantity <= MAX_QUANTITY) {
          itemToUpdate.quantity = quantity;
          state.totalAmount += difference * (itemToUpdate.productPrice ?? 0);
        } else {
          const clampedDiff = MAX_QUANTITY - itemToUpdate.quantity;
          itemToUpdate.quantity = MAX_QUANTITY;
          state.totalAmount += clampedDiff * (itemToUpdate.productPrice ?? 0);
        }
      }
    },
    removeFromCart(state, action) {
      const payload = action.payload;
      const id = typeof payload === "object" ? (payload.productId ?? payload.productID) : payload;
      const rent_days = typeof payload === "object" ? payload.rent_days : undefined;
      const event_date = typeof payload === "object" ? payload.event_date : undefined;
      const attrsKey = typeof payload === "object" ? selectedAttributesKey(payload.selectedAttributes) : '';
      const itemToRemove = state.items.find(
        (item) =>
          (item.productId ?? item.productID) === id &&
          (rent_days == null || (item.rent_days ?? 1) === rent_days) &&
          (event_date == null || (item.event_date ?? "") === event_date) &&
          (attrsKey === '' || selectedAttributesKey(item.selectedAttributes) === attrsKey)
      );
      if (itemToRemove) {
        state.totalAmount -= (itemToRemove.productPrice ?? 0) * itemToRemove.quantity;
        state.items = state.items.filter(
          (item) =>
            (item.productId ?? item.productID) !== id ||
            (rent_days != null && (item.rent_days ?? 1) !== rent_days) ||
            (event_date != null && (item.event_date ?? "") !== event_date) ||
            (attrsKey !== '' && selectedAttributesKey(item.selectedAttributes) !== attrsKey)
        );
      }
    },
    clearCart(state) {
      state.items = [];
      state.totalAmount = 0;
    },
    hydrateCart(state, action) {
      const merged = new Map();
      for (const item of [...state.items, ...(Array.isArray(action.payload) ? action.payload : [])].slice(0, 100)) {
        const productId = item.productId ?? item.productID;
        if (productId == null) continue;
        const quantity = Math.min(MAX_QUANTITY, Math.max(1, Number(item.quantity) || 1));
        const normalized = { ...item, productId, productID: productId, quantity };
        const key = cartItemKey(normalized);
        const existing = merged.get(key);
        merged.set(key, existing && existing.quantity >= quantity ? existing : normalized);
      }
      state.items = [...merged.values()].slice(0, 50);
      state.totalAmount = state.items.reduce(
        (total, item) => total + (Number(item.productPrice) || 0) * item.quantity,
        0
      );
    },
  },
});

export const { addToCart, removeFromCart, updateQuantity, clearCart, hydrateCart } = cartSlice.actions;

export const selectCartItems = (state) => state.cart.items;
export const selectCartTotalAmount = (state) => state.cart.totalAmount;

export default cartSlice.reducer;
